import { inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { deleteDoc, setDoc } from 'firebase/firestore';
import { Guid } from 'guid-typescript';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AuthService } from '@auth';
import { FirestoreService } from './firestore.service';
import { IReplicableEntity } from '@shared/models/replicable-entity';
import { Metadata } from '@shared/models/metadata';
import { NativeServiceSyncStatus } from '@shared/models/native-service-syncable';
import { getEnvironmentInfo } from '@shared/helpers/get-environment-info';
import { EntityRegistry } from './replication/entity-registry.service';
import { ReplicationManager } from './replication/replication-manager.service';
import { EntityConfig, ReplicationOperation } from './replication/entity-config';

/**
 * Abstract base class for all replicable entity services.
 *
 * Provides:
 * - Creating/updating entities with automatic metadata population
 * - Firestore (IndexedDB offline-first) persistence as the source of truth
 * - **Opt-in** event-driven replication to the Desktop app
 *
 * Replication is opt-in via {@link replicationEnabled}. Entities that do not
 * opt in (e.g. cash registers, status logs) keep their previous behaviour
 * exactly — they only persist to Firestore and emit no replication events.
 * This keeps the Phase 1 scope (cash transactions only) free of regressions.
 *
 * @example
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class CashTransactionService extends BaseReplicableService<CashInCashRegister | CashOutCashRegister> {
 *   protected readonly entityType = 'cashTransaction';
 *   protected readonly endpoint = 'CashTransactions';
 *   protected readonly firestoreCollection = 'cashTransactions';
 *   protected override readonly replicationEnabled = true;
 * }
 * ```
 *
 * @see README.md - Generic Data Replication Pattern
 */
@Injectable()
export abstract class BaseReplicableService<T extends IReplicableEntity> {
  /**
   * Fires after any replicable entity is written or deleted locally, with the
   * entity type. Static so cross-cutting listeners (e.g. the preferred-organization
   * pending-records flag, US 27702) can observe every subclass without injecting
   * each one.
   */
  public static readonly localWriteCompleted$ = new Subject<string>();

  /** Unique entity type, e.g. `cashTransaction`, `salesOrderV3`. */
  protected abstract readonly entityType: string;

  /** Native Service API endpoint, e.g. `CashTransactions`. */
  protected abstract readonly endpoint: string;

  /** Firestore collection name, e.g. `cashTransactions`. */
  protected abstract readonly firestoreCollection: string;

  /**
   * Opt into the generic event-driven replication pipeline. Defaults to
   * `false` so existing entities are untouched. When `true`, the service
   * self-registers in the {@link EntityRegistry} and emits replication
   * events on create/update/delete.
   */
  protected readonly replicationEnabled: boolean = false;

  /** Recovery ordering hint when registered. Lower restores first. */
  protected readonly syncPriority: number = 1;

  /**
   * Default `metadata.syncInfo.syncStatus` for newly created entities.
   * Kept as the legacy `Pending` string by default so non-replicating
   * entities (cash registers, status logs) are unchanged. Replicating
   * services override this with the {@link ReplicationSyncStatus} vocabulary.
   */
  protected readonly defaultSyncStatus: string =
    NativeServiceSyncStatus[NativeServiceSyncStatus.Pending];

  protected authService = inject(AuthService);
  protected firestoreService = inject(FirestoreService);
  protected deviceService = inject(DeviceDetectorService);
  protected entityRegistry = inject(EntityRegistry);
  protected replicationManager = inject(ReplicationManager);

  constructor() {
    // Defer to a microtask: subclass field initializers (entityType,
    // replicationEnabled, ...) run *after* this constructor, so reading them
    // here directly would see `undefined`. The microtask runs once the full
    // construction chain has completed.
    queueMicrotask(() => {
      if (this.replicationEnabled) {
        this.entityRegistry.register(this.getEntityConfig());
      }
    });
  }

  /** The {@link EntityConfig} this service contributes to the registry. */
  protected getEntityConfig(): EntityConfig {
    return {
      entityType: this.entityType,
      endpoint: this.endpoint,
      firestoreCollection: this.firestoreCollection,
      replicateWhenOffline: true,
      syncPriority: this.syncPriority
    };
  }

  /**
   * Create an entity: populate metadata, persist to Firestore (the local
   * source of truth), then replicate to the Desktop if enabled.
   */
  async create(entity: T): Promise<T> {
    this.ensureValidMetadata(entity);
    await this.saveToFirestore(entity);
    if (this.replicationEnabled && this.shouldTriggerReplication(entity)) {
      this.emitReplicationEvent('create', entity);
    }
    BaseReplicableService.localWriteCompleted$.next(this.entityType);
    return entity;
  }

  /**
   * Update an entity: stamp `updatedDate`/`updatedBy`, persist, then
   * replicate the new state so the Desktop mirror stays current (the
   * recovery conflict rule depends on `updatedDate`).
   */
  async update(entity: T): Promise<T> {
    this.ensureValidMetadata(entity);
    entity.metadata!.updatedDate = this.firestoreService.serverDateTime();
    entity.metadata!.updatedBy = this.getSanitizedUserProfile();
    await this.saveToFirestore(entity);
    if (this.replicationEnabled && this.shouldTriggerReplication(entity)) {
      this.emitReplicationEvent('update', entity);
    }
    BaseReplicableService.localWriteCompleted$.next(this.entityType);
    return entity;
  }

  /**
   * Delete an entity from Firestore and propagate the deletion to the
   * Desktop mirror (idempotent on the Desktop side).
   */
  async delete(localId: string): Promise<void> {
    const entity = await this.getFromFirestore(localId);
    if (entity && this.replicationEnabled) {
      this.emitReplicationEvent('delete', entity);
    }
    await this.deleteFromFirestore(localId);
    BaseReplicableService.localWriteCompleted$.next(this.entityType);
  }

  /**
   * Ensure an entity has valid metadata, filling in any missing fields.
   */
  protected ensureValidMetadata(entity: T): void {
    const metadata = entity.metadata ?? new Metadata();

    metadata.localId = metadata.localId ?? Guid.create().toString();
    metadata.createdDate = metadata.createdDate ?? this.firestoreService.serverDateTime();
    metadata.createdBy = metadata.createdBy ?? this.getSanitizedUserProfile();
    metadata.organizationInfo = metadata.organizationInfo ?? this.getOrganizationInfo();
    metadata.syncInfo = metadata.syncInfo ?? {
      syncStatus: this.defaultSyncStatus
    };
    metadata.deviceInfo = metadata.deviceInfo ?? this.deviceService.getDeviceInfo();
    metadata.environmentInfo = metadata.environmentInfo ?? getEnvironmentInfo();

    entity.metadata = metadata;
  }

  /**
   * Whether a mutation of this entity should be replicated to the Desktop.
   *
   * Client-first entities (cash transactions) are created locally first and
   * must always be mirrored so the Desktop is a complete recovery source —
   * including the `Syncing`/`Synced` status transitions the recovery
   * conflict rule relies on. Also replicate while offline regardless.
   */
  protected shouldTriggerReplication(entity: T): boolean {
    return (
      !navigator.onLine ||
      this.getEntityConfig().replicateWhenOffline ||
      entity.metadata?.syncInfo?.syncStatus ===
      NativeServiceSyncStatus[NativeServiceSyncStatus.Pending]
    );
  }

  protected emitReplicationEvent(operation: ReplicationOperation, entity: T): void {
    this.replicationManager.emitReplicationEvent({
      entityType: this.entityType,
      operation,
      entity,
      firestoreCollection: this.firestoreCollection,
      timestamp: new Date()
    });
  }

  /**
   * Get the document ID for an entity. Defaults to `metadata.localId`.
   * Subclasses can override (e.g. cash registers use the server id).
   */
  protected getDocumentId(entity: T): string {
    return entity.metadata!.localId;
  }

  /**
   * Save an entity to Firestore (IndexedDB offline-first storage).
   */
  protected async saveToFirestore(entity: T): Promise<void> {
    const documentRef = this.firestoreService.doc(
      `${this.firestoreCollection}/${this.getDocumentId(entity)}`
    );
    // Convert to plain object to avoid Firestore serialization issues with
    // class instances.
    const plainObject = JSON.parse(JSON.stringify(entity));
    await this.firestoreService.settleWrite(
      () => setDoc(documentRef, plainObject),
      documentRef.path
    );
  }

  /**
   * Update an entity in Firestore (full document replacement).
   */
  protected async updateInFirestore(entity: T): Promise<void> {
    await this.saveToFirestore(entity);
  }

  /**
   * Delete an entity document from Firestore by its localId.
   */
  protected async deleteFromFirestore(localId: string): Promise<void> {
    const documentRef = this.firestoreService.doc(
      `${this.firestoreCollection}/${localId}`
    );
    await this.firestoreService.settleWrite(
      () => deleteDoc(documentRef),
      documentRef.path
    );
  }

  /**
   * Get an entity from Firestore by its localId.
   */
  protected async getFromFirestore(localId: string): Promise<T | undefined> {
    const doc = await this.firestoreService.getDocSnapshotAsync(
      `${this.firestoreCollection}/${localId}`
    );
    return doc?.exists() ? (doc.data() as T) : undefined;
  }

  /**
   * Check if an entity exists in Firestore.
   */
  async existsInFirestore(localId: string): Promise<boolean> {
    const entity = await this.getFromFirestore(localId);
    return !!entity;
  }

  /**
   * Get the current user's profile without the picture field.
   */
  protected getSanitizedUserProfile(): any {
    const profile = { ...this.authService.userProfile };
    delete profile.picture;
    return profile;
  }

  /**
   * Get the current organization info.
   */
  protected getOrganizationInfo(): { id: string; name: string } {
    return {
      id: this.authService.currentTenant,
      name: this.authService.currentOrganization!.name
    };
  }
}
